import { useCallback, useEffect, useState } from "react";

/**
 * Auto-save a text draft to localStorage, keyed by `scope`. Hydrates the saved
 * value on mount, persists on every change, and removes the entry when the text
 * is cleared. Pass `null` as the scope to disable persistence (state-only).
 *
 * Scope examples: `post:my-group-slug`, `comment:123`, `comment:123:45`.
 */
export function useDraft(scope: string | null) {
  const storageKey = scope ? `theta:draft:${scope}` : null;
  const [value, setValue] = useState("");
  // Tracks which key the current `value` was hydrated from, so the persist
  // effect doesn't fire (and wipe the saved draft) before hydration completes.
  const [hydratedKey, setHydratedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!storageKey) return;
    let saved = "";
    try {
      saved = localStorage.getItem(storageKey) ?? "";
    } catch {
      // localStorage unavailable (SSR, privacy mode) — fall back to empty.
    }
    // localStorage is unavailable during SSR; reading it in render would produce
    // a hydration mismatch, so the draft is hydrated on mount instead.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(saved);
    setHydratedKey(storageKey);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || hydratedKey !== storageKey) return;
    try {
      if (value) localStorage.setItem(storageKey, value);
      else localStorage.removeItem(storageKey);
    } catch {
      // Ignore write failures (quota, privacy mode).
    }
  }, [storageKey, hydratedKey, value]);

  const clear = useCallback(() => {
    setValue("");
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        // Ignore.
      }
    }
  }, [storageKey]);

  /**
   * Whether `value` is the saved draft yet. It is `""` both before hydration
   * and when there is nothing saved, and a caller that writes into an empty
   * box on mount — the reply composer's @-tag prefill (OSO-596) — has to tell
   * those two apart or it overwrites the draft it was supposed to leave alone.
   * With persistence off there is nothing to wait for.
   */
  const hydrated = storageKey === null || hydratedKey === storageKey;

  return { value, setValue, clear, hydrated };
}
